import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import type { ApprovalInputDetailsProps } from '@heidi/agents';
import {
  ApprovalInputDetails as ApprovalInputDetailsView,
  DeletePatientRow,
  MergeSessionRow,
} from '@heidi/agents';
import { patientProfileDisplayName } from '@heidi/agents/contracts';
import { useQueryPatientProfileById } from '@heidi/patients/data';
import { useQueryDrawerLinkedSessions, useQuerySessionById } from '@heidi/session/data';
import type { ReactNode } from 'react';

/** Resolves one merge row's session name + created time by id. */
const MergeSessionApprovalRow = ({ sessionId }: { sessionId: string }) => {
  const { data } = useQuerySessionById({
    sessionId,
    select: (session) => ({
      name: session?.session_name,
      createdTimeLocal: session?.created_time_local,
    }),
  });
  return (
    <MergeSessionRow
      sessionId={sessionId}
      name={data?.name}
      createdTimeLocal={data?.createdTimeLocal}
    />
  );
};

const DeletePatientApprovalRow = ({
  patientProfileId,
  deleteLinkedSessions,
}: {
  patientProfileId: string;
  deleteLinkedSessions: boolean | undefined;
}) => {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: profile } = useQueryPatientProfileById(patientProfileId, {
    isAuthenticated,
  });
  const { data, isLoading, isError } = useQueryDrawerLinkedSessions(patientProfileId);
  return (
    <DeletePatientRow
      patientProfileId={patientProfileId}
      name={patientProfileDisplayName(profile)}
      sessions={data?.sessions}
      status={isLoading ? 'loading' : isError ? 'error' : 'ready'}
      deleteLinkedSessions={deleteLinkedSessions}
    />
  );
};

const renderMergeSessionRow = (sessionId: string) => (
  <MergeSessionApprovalRow sessionId={sessionId} />
);

const renderDeletePatientRow = (
  patientProfileId: string,
  deleteLinkedSessions: boolean | undefined
) => (
  <DeletePatientApprovalRow
    key={patientProfileId}
    patientProfileId={patientProfileId}
    deleteLinkedSessions={deleteLinkedSessions}
  />
);

/** The approval card's body, bound to the session and patient reads its rows need. */
export const ApprovalInputDetails = ({
  approval,
}: ApprovalInputDetailsProps): ReactNode => (
  <ApprovalInputDetailsView
    approval={approval}
    renderMergeSessionRow={renderMergeSessionRow}
    renderDeletePatientRow={renderDeletePatientRow}
  />
);
